import React, { useRef, useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Typography, Slider
} from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';

const SIZE = 300;

export default function FotoCropModal({ open, imageSrc, onClose, onConfirm }) {
    const canvasRef = useRef(null);
    const imgRef = useRef(null);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [scale, setScale] = useState(1);
    const [loaded, setLoaded] = useState(false);
    const dragging = useRef(false);
    const lastPos = useRef({ x: 0, y: 0 });
    const lastTouchDist = useRef(null);

    useEffect(() => {
        if (!open || !imageSrc) return;
        setLoaded(false);
        const img = new Image();
        img.onload = () => {
            imgRef.current = img;
            const minDim = Math.min(img.naturalWidth, img.naturalHeight);
            const initialScale = SIZE / minDim;
            setScale(initialScale);
            setOffset({
                x: (SIZE - img.naturalWidth * initialScale) / 2,
                y: (SIZE - img.naturalHeight * initialScale) / 2,
            });
            setLoaded(true);
        };
        img.src = imageSrc;
    }, [open, imageSrc]);

    useEffect(() => {
        if (!loaded || !canvasRef.current || !imgRef.current) return;
        const ctx = canvasRef.current.getContext('2d');
        const img = imgRef.current;
        ctx.clearRect(0, 0, SIZE, SIZE);

        // Image clipped to circle
        ctx.save();
        ctx.beginPath();
        ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(img, offset.x, offset.y, img.naturalWidth * scale, img.naturalHeight * scale);
        ctx.restore();

        // Dark overlay outside circle
        ctx.save();
        ctx.fillStyle = 'rgba(0,0,0,0.55)';
        ctx.beginPath();
        ctx.rect(0, 0, SIZE, SIZE);
        ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2, 0, Math.PI * 2, true);
        ctx.fill();
        ctx.restore();

        // Border
        ctx.beginPath();
        ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2 - 1, 0, Math.PI * 2);
        ctx.strokeStyle = '#1ABB9C';
        ctx.lineWidth = 2;
        ctx.stroke();
    }, [loaded, offset, scale]);

    const handleMouseDown = (e) => {
        dragging.current = true;
        lastPos.current = { x: e.clientX, y: e.clientY };
    };
    const handleMouseMove = (e) => {
        if (!dragging.current) return;
        const dx = e.clientX - lastPos.current.x;
        const dy = e.clientY - lastPos.current.y;
        lastPos.current = { x: e.clientX, y: e.clientY };
        setOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
    };
    const handleMouseUp = () => { dragging.current = false; };

    const handleWheel = (e) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.92 : 1.08;
        setScale(prev => Math.min(Math.max(prev * delta, 0.2), 8));
    };

    const handleTouchStart = (e) => {
        if (e.touches.length === 1) {
            dragging.current = true;
            lastPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        }
    };
    const handleTouchMove = (e) => {
        e.preventDefault();
        if (e.touches.length === 1 && dragging.current) {
            const dx = e.touches[0].clientX - lastPos.current.x;
            const dy = e.touches[0].clientY - lastPos.current.y;
            lastPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            setOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
        } else if (e.touches.length === 2) {
            const dist = Math.hypot(
                e.touches[0].clientX - e.touches[1].clientX,
                e.touches[0].clientY - e.touches[1].clientY
            );
            if (lastTouchDist.current) {
                const delta = dist / lastTouchDist.current;
                setScale(prev => Math.min(Math.max(prev * delta, 0.2), 8));
            }
            lastTouchDist.current = dist;
        }
    };
    const handleTouchEnd = () => {
        dragging.current = false;
        lastTouchDist.current = null;
    };

    const handleConfirm = () => {
        if (!canvasRef.current || !imgRef.current) return;
        const out = document.createElement('canvas');
        out.width = 300;
        out.height = 300;
        const ctx = out.getContext('2d');
        const ratio = 300 / SIZE;
        ctx.beginPath();
        ctx.arc(150, 150, 150, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(
            imgRef.current,
            offset.x * ratio, offset.y * ratio,
            imgRef.current.naturalWidth * scale * ratio,
            imgRef.current.naturalHeight * scale * ratio
        );
        out.toBlob(blob => onConfirm(blob), 'image/jpeg', 0.92);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontWeight: 700 }}>Ajustar Foto de Perfil</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, pb: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                    Arraste para posicionar · Scroll ou slider para zoom
                </Typography>
                <canvas
                    ref={canvasRef}
                    width={SIZE}
                    height={SIZE}
                    style={{ cursor: 'grab', touchAction: 'none', maxWidth: '100%', userSelect: 'none' }}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onWheel={handleWheel}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', px: 1 }}>
                    <ZoomOutIcon sx={{ color: 'text.secondary' }} />
                    <Slider
                        value={scale}
                        min={0.2}
                        max={8}
                        step={0.01}
                        onChange={(_, v) => setScale(v)}
                        sx={{ flex: 1, color: '#1ABB9C' }}
                    />
                    <ZoomInIcon sx={{ color: 'text.secondary' }} />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} color="inherit">Cancelar</Button>
                <Button onClick={handleConfirm} variant="contained" disabled={!loaded}
                    sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' } }}>
                    Usar esta foto
                </Button>
            </DialogActions>
        </Dialog>
    );
}
